/**
 * jobBuckets.js — Fixed, automatic buckets for the Jobs list.
 *
 * Replaces the hand-filled job_groups system, which every user left empty
 * because filing 70 jobs by hand buys nothing. These buckets need no setup:
 * a job lands in exactly one, derived from its status and its dates.
 *
 * Two rules make the counts trustworthy:
 *   1. FIRST MATCH WINS — bucketOf walks BUCKETS in order and returns one key.
 *   2. NOTHING FALLS THROUGH — 'other' catches any status the ladder misses,
 *      so a job can never disappear from the list because of a typo'd status.
 *
 * Everything here is pure and date-injectable so the rules can be tested
 * without freezing the clock.
 */
import { normalizeSnapshot, deptLeads } from './calcEngine';

/** Local-time YYYY-MM-DD. Never use toISOString here — it shifts by timezone. */
export function todayKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Trim a date or timestamp down to its YYYY-MM-DD day. */
export function dayKey(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

/** Shift a YYYY-MM-DD key by n calendar days, staying in local time. */
export function addDays(key, n) {
  const [y, m, d] = key.split('-').map(Number);
  return todayKey(new Date(y, m - 1, d + n));
}

/**
 * Every day this job shoots, sorted. Multi-day jobs carry shoot_dates; single
 * day jobs only have shoot_date. Returns [] when nothing is on the calendar.
 */
export function shootDays(job) {
  const many = Array.isArray(job?.shootDates) ? job.shootDates.map(dayKey).filter(Boolean) : [];
  if (many.length) return [...new Set(many)].sort();
  const one = dayKey(job?.shootDate);
  return one ? [one] : [];
}

/** The next shoot day on or after `today`, or null if they are all behind us. */
export function nextShootDay(job, today) {
  return shootDays(job).find((d) => d >= today) ?? null;
}

/** True once a crew split has actually been saved on the job. */
export function hasCrewSplit(job) {
  const ns = normalizeSnapshot(job?.calcSnapshot);
  return !!(ns.savedAt || ns.originators?.length > 0 || deptLeads(ns.prod).length || deptLeads(ns.post).length);
}

// Statuses grouped by where they sit in the pipeline.
const PRE_SHOOT = ['intake', 'quoted', 'confirmed'];
const IN_POST = ['shooting', 'post_production', 'partial_delivery'];
// Anything that can still have an upcoming shoot day on the calendar.
const PRE_DELIVERY = ['intake', 'quoted', 'confirmed', 'shooting'];
// The window "This week" covers, counted forward from today.
export const WEEK_HORIZON_DAYS = 7;

/**
 * Shoot happened, job is live, nobody saved the split. Carried over from the
 * old JobList helper — it also drives the ⚡ chip on the card.
 */
export function needsSplit(job, today = todayKey()) {
  const days = shootDays(job);
  const shootStarted = days.length > 0 && days[0] <= today;
  const isActive = ['confirmed', ...IN_POST, 'delivered', 'invoiced'].includes(job?.status);
  return shootStarted && isActive && !hasCrewSplit(job);
}

/**
 * The three ways a live job goes wrong quietly. Each is a date that passed
 * without the status following it.
 */
export function attentionReasons(job, today = todayKey()) {
  const reasons = [];
  const days = shootDays(job);
  const lastShoot = days.length ? days[days.length - 1] : null;

  if (lastShoot && lastShoot < today && PRE_SHOOT.includes(job?.status)) {
    reasons.push('shoot date passed, status never moved');
  }
  const due = dayKey(job?.deliveryDate);
  if (due && due < today && ['confirmed', ...IN_POST].includes(job?.status)) {
    reasons.push('past its delivery date');
  }
  if (needsSplit(job, today)) {
    reasons.push('shot, but no crew split saved');
  }
  return reasons;
}

/**
 * Bucket definitions, in display order. `bucketOf` depends on this order.
 *
 *   dim              — rendered muted; history, not work
 *   defaultCollapsed — closed until the user opens it (remembered after that)
 *   months           — sub-grouped by month instead of listed flat
 */
export const BUCKETS = [
  {
    key: 'pinned',
    label: 'Pinned',
    description: 'Live jobs you pinned. A pin outranks the pipeline buckets but not archived or void — old pins stay filed under Archived instead of burying today’s work.',
  },
  {
    key: 'attention',
    label: 'Needs attention',
    tone: 'warning',
    description: 'A date passed and the job did not follow: shoot day gone by with the status still pre-shoot, delivery date missed, or a finished shoot with no crew split saved.',
  },
  {
    key: 'week',
    label: 'This week',
    description: `Shooting in the next ${WEEK_HORIZON_DAYS} days, whatever the status.`,
  },
  {
    key: 'leads',
    label: 'New leads',
    description: 'Intake. Came in, not quoted yet.',
  },
  {
    key: 'quotes',
    label: 'Quotes out',
    description: 'Quoted and waiting on the client.',
  },
  {
    key: 'undated',
    label: 'Needs a date',
    description: 'Confirmed, but nothing on the calendar yet.',
  },
  {
    key: 'upcoming',
    label: 'Upcoming',
    description: `Confirmed shoots more than ${WEEK_HORIZON_DAYS} days out.`,
  },
  {
    key: 'post',
    label: 'In post',
    description: 'Shooting, post production, or partially delivered.',
  },
  {
    key: 'payment',
    label: 'Awaiting payment',
    description: 'Delivered or invoiced, money not in yet.',
  },
  {
    key: 'paid',
    label: 'Paid',
    defaultCollapsed: true,
    description: 'Paid and finished, not archived yet.',
  },
  {
    key: 'other',
    label: 'Other',
    defaultCollapsed: true,
    description: 'Anything the buckets above did not claim. If jobs pile up here, a status is missing from the rules.',
  },
  {
    key: 'void',
    label: 'Void',
    dim: true,
    defaultCollapsed: true,
    description: 'Voided jobs. Kept for the record.',
  },
  {
    key: 'archived',
    label: 'Archived',
    dim: true,
    defaultCollapsed: true,
    months: true,
    description: 'Archived jobs, newest month first.',
  },
];

export const BUCKET_BY_KEY = Object.fromEntries(BUCKETS.map((b) => [b.key, b]));

/** Buckets that are history rather than work — excluded from the active count. */
export const INACTIVE_BUCKETS = new Set(['archived', 'void']);

/**
 * Which bucket a job belongs to. Returns exactly one key, always.
 *
 * @param {object} job
 * @param {object} [opts]
 * @param {string} [opts.today]     YYYY-MM-DD, injectable for tests
 * @param {(id:string)=>boolean} [opts.isPinned]
 */
export function bucketOf(job, { today = todayKey(), isPinned } = {}) {
  if (!job) return 'other';
  const status = job.status;

  // Dead jobs sort as dead even when pinned. Pins go stale — every one of the
  // 12 on this account pointed at an archived job — and letting them jump the
  // queue buries the live work under months-old listings.
  if (status === 'archived') return 'archived';
  if (status === 'void') return 'void';

  // On a live job a pin is an explicit "keep this in front of me", so it beats
  // the pipeline buckets below.
  if (isPinned?.(job.id)) return 'pinned';

  if (status === 'paid') return 'paid';

  if (attentionReasons(job, today).length > 0) return 'attention';

  const next = nextShootDay(job, today);
  if (next && next <= addDays(today, WEEK_HORIZON_DAYS) && PRE_DELIVERY.includes(status)) return 'week';

  if (status === 'intake') return 'leads';
  if (status === 'quoted') return 'quotes';
  if (status === 'confirmed') return shootDays(job).length ? 'upcoming' : 'undated';
  if (IN_POST.includes(status)) return 'post';
  if (status === 'delivered' || status === 'invoiced') return 'payment';

  return 'other';
}

/**
 * Slice a job list into display-ordered buckets, dropping the empty ones.
 * @returns {Array<{...bucketDef, jobs: object[]}>}
 */
export function bucketize(jobs, opts = {}) {
  const map = new Map(BUCKETS.map((b) => [b.key, []]));
  for (const job of jobs || []) {
    map.get(bucketOf(job, opts)).push(job);
  }
  return BUCKETS
    .map((b) => ({ ...b, jobs: map.get(b.key) }))
    .filter((b) => b.jobs.length > 0);
}
